import { Zoom } from './shared/model/docavea-viewer-interface';
import { loadPdfPayload, pdfViewer } from './main';
import { Utils } from '../src/shared/extensions/utils.extension';
import { viewChanged } from './view-changed';
import * as _ from 'lodash';

let viewerContainerEl: HTMLElement;
let pageContainerEl: HTMLElement;
let timeStamp = 0;
const borderCompensationFactor = 9;
let pinchInProgress = false;
const center = { x: 0, y: 0 };
let startDistance = 1;
let touchScale = 1;

export function initPinchZoom() {
    const _viewerContainerEl = document.getElementById('viewerContainer');

    if (_viewerContainerEl !== null) {
        viewerContainerEl = _viewerContainerEl as HTMLElement;
    } else {
        console.error(`getElementById('viewerContainer') failed`);
        return;
    }

    // gesture events only happen on MacOS using the touch pad
    if (Utils.isMacOs() || Utils.isIos()) {
        viewerContainerEl.addEventListener('gesturestart', (event) => gestureStart(event));
        viewerContainerEl.addEventListener('gesturechange', (event) => gestureChange(event));
        viewerContainerEl.addEventListener('gestureend', (event) => gestureEnd(event));
        // gesture events only give us the mouse position as global coordinates, so to get local coordinats we track mouse events
        viewerContainerEl.addEventListener('mousemove', (event) => mouseMove(event));
    } else {
        viewerContainerEl.addEventListener('touchstart', (event) => touchStart(event));
        viewerContainerEl.addEventListener('touchmove', (event) => touchMove(event));
        viewerContainerEl.addEventListener('touchend', (event) => touchEnd(event));
    }
}

function mouseMove(event: MouseEvent) {
    center.x = event.clientX;
    center.y = event.clientY;
}

function gestureStart(event) {
    // console.log('gestureStart');

    // if (Utils.isIos()) { return; }
    event.preventDefault();
    // console.log('gestureStart', event);

    pinchInProgress = true;

    if (!setPageContainerEl()) {
        return;
    }
    // Reset transforms
    viewerContainerEl.style.transform = `scale(1)`;
    // set zoom origin
    viewerContainerEl.style.transformOrigin = `${center.x}px ${center.y}px`;
}

function gestureChange(event) {
    // if (Utils.isIos()) { return; }
    event.preventDefault();
    // console.log('gestureChange', event);

    const clampedEventScale = _.clamp(event.scale, loadPdfPayload.minScale / pdfViewer.currentScaleValue, loadPdfPayload.maxScale / pdfViewer.currentScaleValue);

    if (timeStamp === 0) {
        timeStamp = event.timeStamp - 101;
    }
    if ((event.timeStamp - timeStamp) > 100) {
        timeStamp = event.timeStamp;
        // console.log('gestureChange', event, clampedEventScale);
    }

    viewerContainerEl.style.transform = `scale(${clampedEventScale})`;
}

function gestureEnd(event) {
    // if (Utils.isIos()) { return; }
    event.preventDefault();
    // console.log('gestureEnd', event);

    pinchZoomEnd(event.scale);
}

function touchStart(event: TouchEvent) {
    if (event.touches.length === 2 && Utils.isTouchDevice()) {
        // console.log('touchstart', event);
        event.preventDefault();
        pinchInProgress = true;

        startDistance = Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY);

        if (!setPageContainerEl()) {
            return;
        }
        // Reset transforms
        viewerContainerEl.style.transform = `scale(1)`;
        // set zoom origin
        const centerX = event.touches[0].clientX + (event.touches[0].clientX - event.touches[1].clientX) / 2;
        const centerY = event.touches[0].clientY + (event.touches[0].clientY - event.touches[1].clientY) / 2;
        viewerContainerEl.style.transformOrigin = `${centerX}px ${centerY}px`;
    }
}

function touchMove(event: TouchEvent) {
    if (!pinchInProgress || event.touches.length !== 2 || !Utils.isTouchDevice()) {
        return;
    }
    if (event.cancelable) {
        event.preventDefault();
    }

    const distance = Math.hypot(event.touches[0].clientX - event.touches[1].clientX, event.touches[0].clientY - event.touches[1].clientY);
    touchScale = distance / startDistance;

    const clampedEventScale = _.clamp(touchScale, loadPdfPayload.minScale / pdfViewer.currentScaleValue, loadPdfPayload.maxScale / pdfViewer.currentScaleValue);

    if (timeStamp === 0) {
        timeStamp = event.timeStamp - 101;
    }
    if ((event.timeStamp - timeStamp) > 100) {
        timeStamp = event.timeStamp;
        // console.log('touchMove', event, touchScale, clampedEventScale);
    }

    viewerContainerEl.style.transform = `scale(${clampedEventScale})`;
}

async function touchEnd(event: TouchEvent) {
    if (!pinchInProgress || pageContainerEl === undefined || !Utils.isTouchDevice()) { 
        return; 
    }
    if (event.cancelable) {
        event.preventDefault();
    }
    // console.log('touchend', event);

    pinchZoomEnd(touchScale);
}

async function pinchZoomEnd(scale: number) {
    pinchInProgress = false;

    // calculate scale
    const clampedEventScale = _.clamp(scale, loadPdfPayload.minScale / pdfViewer.currentScaleValue, loadPdfPayload.maxScale / pdfViewer.currentScaleValue);
    viewerContainerEl.style.transform = `scale(${clampedEventScale})`;
    const newScale = clampedEventScale * pdfViewer.currentScaleValue;
    // Get page 1 rect before we reset transforms
    const page1Rect = pageContainerEl.getClientRects()[0];

    // Reset transforms
    viewerContainerEl.style.transform = `scale(1)`;

    // calculate new offsets
    // borderOffset comes from the page border which is set to 9px. When applying CSS scale this border is scaled.
    // when resetting css scale and setting pdfViewer.currentScaleValue, the gap is set back to 9px. gapOffset compensates for that.
    // for some unknown reason this needs to be a bit different between browsers
    // the -1 in (pdfViewer.currentPageNumber - 1) is because the is no border at the top of page 1
    // the -1 in (clampedEventScale - 1) is beacuse the border ends back i 9px after CSS scaling is set back to 1
    const borderOffset = (pdfViewer.currentPageNumber - 1) * borderCompensationFactor * (newScale - 1);
    const containerRect = viewerContainerEl.getClientRects()[0];
    const scrollLeft = containerRect.left - page1Rect.left;
    const scrollTop = containerRect.top - page1Rect.top - borderOffset;

    // console.log('newScale', newScale, 'pdfViewer.currentScaleValue', pdfViewer.currentScaleValue, 'clampedEventScale', clampedEventScale);
    pdfViewer.currentScaleValue = newScale;
    loadPdfPayload.scalePayload.scale = newScale;
    loadPdfPayload.scalePayload.zoom = Zoom.SCALE;

    viewerContainerEl.scrollTo(scrollLeft, scrollTop);

    await viewChanged(false, false);
}

function setPageContainerEl() {
    const _pageContainerEl = viewerContainerEl.querySelector(`div[data-page-number='1']`);
    if (_pageContainerEl) {
        pageContainerEl = _pageContainerEl as HTMLElement;
        return true;
    } else {
        console.error(`querySelector({pdfViewer.currentPageNumber}) failed.`);
        return false;
    }
}
